import { stableId } from '@dovefiniscono/sync';
import { useCollection, useStore } from '@dovefiniscono/sync/react';
import { useEffect, useState } from 'react';
import { addDays, today as todayISO } from '../lib/dates';
import { formatCents } from '../lib/money';
import { copyFor, due } from './recurrence';
import { pendingRuns, shares } from './rules';

/** The id of a rule applied to an event: the same on every device, so it is applied once. */
export const runId = (ruleId: string, eventId: string) => stableId(`rule:${ruleId}:${eventId}`);

/**
 * Makes what the automations promise while the app is open: the copies of recurrences that are
 * due, and what the rules do when their event happens. Every record made here has an id made from
 * what caused it, so another device doing the same at the same time makes the same record, and
 * nothing is doubled.
 */
export function AutomationRunner() {
  const store = useStore();
  const recurrences = useCollection('recurrences');
  const rules = useCollection('rules');
  const runs = useCollection('ruleRuns');
  const invoices = useCollection('invoices');
  const transactions = useCollection('transactions');
  const tasks = useCollection('tasks');
  const categories = useCollection('categories');
  const [today, setToday] = useState(todayISO);

  // Past midnight the new day's copies are due.
  useEffect(() => {
    const timer = setInterval(() => setToday(todayISO()), 60_000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    for (const recurrence of recurrences) {
      const { dates, until } = due(recurrence, today);
      if (dates.length === 0) continue;
      dates.forEach((day, index) => {
        const copy = copyFor(recurrence, day, Date.now() + index);
        if (!copy) return;
        try {
          store.create(
            copy.collection,
            copy.fields as never,
            stableId(`recurrence:${recurrence.id}:${day}`),
          );
        } catch {
          // A template that no longer fits (an account deleted): skip this copy.
        }
      });
      store.update('recurrences', recurrence.id, { generatedUntil: until });
    }
  }, [recurrences, today, store]);

  useEffect(() => {
    if (rules.length === 0) return;
    const done = new Set(runs.map((run) => run.id));
    const parentOf = new Map(
      categories.flatMap((category) =>
        category.parentId ? [[category.id, category.parentId] as const] : [],
      ),
    );
    const pending = pendingRuns(
      rules,
      { invoices, transactions, tasks, parentOf },
      (ruleId, eventId) => done.has(runId(ruleId, eventId)),
    );
    for (const { rule, event } of pending) {
      const id = runId(rule.id, event.id);
      const parts: string[] = [];
      try {
        if (rule.action === 'setAside') {
          for (const part of shares(event.amount, rule.params.allocations ?? [])) {
            const goal = store.get('savingsGoals', part.goalId);
            if (!goal) continue;
            store.update('savingsGoals', goal.id, { saved: goal.saved + part.cents });
            parts.push(`+${formatCents(part.cents)} in ${goal.name}`);
          }
        } else if (rule.params.amount && rule.params.amount > 0) {
          store.create(
            'invoices',
            {
              client: rule.params.client?.trim() || event.label,
              subject: event.label,
              issuedOn: today,
              amount: rule.params.amount,
              paidOn: null,
              transactionId: null,
              dueOn: addDays(today, 30),
              draft: true,
            },
            stableId(`draft:${rule.id}:${event.id}`),
          );
          parts.push(`bozza di fattura per ${rule.params.client?.trim() || event.label}`);
        }
      } catch {
        parts.push('non riuscita');
      }
      store.create(
        'ruleRuns',
        {
          ruleId: rule.id,
          sourceId: event.id,
          at: new Date().toISOString(),
          summary: `${event.label}: ${parts.join(', ') || 'niente da fare'}`.slice(0, 300),
        },
        id,
      );
    }
  }, [rules, runs, invoices, transactions, tasks, categories, store, today]);

  return null;
}
